import { isDayKey, isTimeOfDay } from './dates'
import { ATTENDANCE_STATUSES, type AttendanceStatus, ROLES, type Role } from './types'

/**
 * Every invariant in one place. The API asserts them at the write boundary; the
 * apps check the same rules before submitting so they can show a friendly
 * message. A violation reaching the API therefore means a bug or a tampered
 * request, never ordinary input — the API answers 400 `invalid`.
 *
 * Validators return a `ConstraintIssue` code (stable, mapped to copy by each
 * app) or `null` when the value is fine.
 */

export const LIMITS = {
  memberName: 60,
  meetingNote: 80,
  reason: 120,
  reasonLabel: 40,
  displayName: 40,
  passwordMin: 6,
  passwordMax: 72,
  usernameMin: 3,
  usernameMax: 32,
  importBatch: 200,
} as const

export type ConstraintIssue =
  | 'required'
  | 'tooLong'
  | 'tooShort'
  | 'badDate'
  | 'badTime'
  | 'badUsername'
  | 'leftBeforeJoined'

/** Collapses inner whitespace and trims — how every free-text name is stored. */
export function cleanText(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function text(value: string, max: number, required: boolean): ConstraintIssue | null {
  const v = cleanText(value)
  if (required && v.length === 0) return 'required'
  if (v.length > max) return 'tooLong'
  return null
}

export const checkMemberName = (v: string) => text(v, LIMITS.memberName, true)
export const checkMeetingNote = (v: string) => text(v, LIMITS.meetingNote, false)
export const checkReason = (v: string) => text(v, LIMITS.reason, false)
export const checkReasonLabel = (v: string) => text(v, LIMITS.reasonLabel, true)
export const checkDisplayName = (v: string) => text(v, LIMITS.displayName, true)

export function checkPassword(v: string): ConstraintIssue | null {
  if (v.length === 0) return 'required'
  if (v.length < LIMITS.passwordMin) return 'tooShort'
  if (v.length > LIMITS.passwordMax) return 'tooLong'
  return null
}

export function checkMeetingDate(v: string): ConstraintIssue | null {
  return isDayKey(v) ? null : 'badDate'
}

export function checkMeetingTime(v: string | null): ConstraintIssue | null {
  if (v === null || v === '') return null
  return isTimeOfDay(v) ? null : 'badTime'
}

export function checkMemberDates(joinedOn: string, leftOn: string | null): ConstraintIssue | null {
  if (!isDayKey(joinedOn)) return 'badDate'
  if (leftOn === null) return null
  if (!isDayKey(leftOn)) return 'badDate'
  return leftOn <= joinedOn ? 'leftBeforeJoined' : null
}

export function isAttendanceStatus(v: unknown): v is AttendanceStatus {
  return typeof v === 'string' && (ATTENDANCE_STATUSES as readonly string[]).includes(v)
}

export function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (ROLES as readonly string[]).includes(v)
}

/** A reason only travels with an absence; anything else stores null. */
export function normalizeReason(status: AttendanceStatus, reason: string | null): string | null {
  if (status !== 'excused' && status !== 'absent') return null
  const v = cleanText(reason ?? '')
  return v.length === 0 ? null : v
}
